import React, { useEffect, useRef } from "react";
import { Input } from "antd";
import { useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../helper/googleMaps";

// Reusable Google Places address input. Wraps an antd <Input> with a Places
// Autocomplete bound to its underlying DOM node, using the shared singleton
// loader options (helper/googleMaps) so it never trips the "Loader must not be
// called again with different options" crash.
//
// Drop-in for a Form.Item: it accepts the injected `value`/`onChange` (so the
// typed/selected text flows into the form). `onPlaceSelected({ address,
// coordinates })` fires on every change — coordinates are the picked place's
// lat/lng on a suggestion, or null on a manual edit (so a stale pin never
// outlives the address it belonged to). Extracted from the inline copies in
// CreateTaskModal / PropertyFormModal so the binding logic lives in one place.
const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder = "Start typing an address…",
  allowClear = true,
  disabled = false,
}) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  // Keep the latest callbacks in refs so the bind effect can depend only on
  // `isLoaded` (Form re-injects a fresh onChange each render; depending on it
  // would rebind the Autocomplete on every keystroke).
  const onChangeRef = useRef(onChange);
  const onPlaceRef = useRef(onPlaceSelected);
  onChangeRef.current = onChange;
  onPlaceRef.current = onPlaceSelected;

  useEffect(() => {
    if (!isLoaded) return undefined;

    let cancelled = false;
    let timerId;
    let listener;
    let autocomplete;

    // The input may live inside a destroyOnClose Modal — poll briefly until both
    // the DOM node and the Places SDK exist, then bind.
    const tryBind = (attempt = 0) => {
      if (cancelled || autocompleteRef.current) return;
      const input = inputRef.current;
      const placesNs = window.google?.maps?.places;
      if (!input || !placesNs?.Autocomplete) {
        if (attempt < 25) timerId = setTimeout(() => tryBind(attempt + 1), 120);
        return;
      }
      autocomplete = new placesNs.Autocomplete(input, {
        types: ["address"],
        fields: ["formatted_address", "geometry", "name"],
      });
      autocompleteRef.current = autocomplete;
      listener = autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const formatted = place?.formatted_address || place?.name || "";
        // Google sets the input's value via the DOM (no React onChange), so push
        // the chosen address into the form ourselves, plus its coordinates.
        if (formatted) onChangeRef.current?.(formatted);
        const loc = place?.geometry?.location;
        onPlaceRef.current?.({
          address: formatted,
          coordinates: loc ? { lat: loc.lat(), lng: loc.lng() } : null,
        });
      });
    };
    tryBind();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      listener?.remove?.();
      if (autocomplete) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocomplete);
      }
      // Google appends the suggestions dropdown to <body> and never removes it.
      document.querySelectorAll(".pac-container").forEach((el) => el.remove());
      autocompleteRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  return (
    <Input
      ref={(node) => {
        // antd v5 Input exposes the underlying <input> via `node.input`.
        inputRef.current =
          node?.input ||
          node?.nativeElement?.querySelector?.("input") ||
          null;
      }}
      value={value}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      onChange={(e) => {
        // Manual typing means the text no longer matches any picked place — sync
        // the form and clear any captured coordinates (null) so they can't go
        // stale. A suggestion is set via the DOM and handled in place_changed.
        onChange?.(e.target.value);
        onPlaceRef.current?.({ address: e.target.value, coordinates: null });
      }}
    />
  );
};

export default AddressAutocomplete;
